import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import _ from "lodash";
import moment from "moment";
import "moment/locale/pt-br";
import {
  LuSearch,
  LuTrash2,
  LuPercent,
  LuX,
  LuCreditCard,
  LuGift,
  LuEllipsis,
  LuLayoutGrid,
  LuList,
} from "react-icons/lu";

import requestApi, { get } from "../../../apirest";
import { auth } from "../../../auth";
import { getColorsProduct } from "../../../services/filter";
import { routeZblInput } from "../../utils/codebars/ValidateZPL";
import {
  buildBaseProductFromBarcode,
  normalizeOrderProduct,
  upsertOrderProduct,
} from "../principal/order/utils/orderProducts";
import Switch from "react-switch";
import { maskCPF } from "../../../utils/mask";
import { categories as PRODUCT_CATEGORIES } from "../../../data/product";
import getProductId from "../../../shared/utils/getProductId";
import DiscountModal from "./components/DiscountModal";
import CustomerModal from "./components/CustomerModal";
import CustomerDataModal from "./components/CustomerDataModal";
import AddressModal from "./components/AddressModal";
import InstallmentModal from "./components/InstallmentModal";
import { INSTALLMENT_METHODS, resolveSaleStatus } from "./saleStatus";
import SaleDetailsSection from "./components/SaleDetailsSection";
import CategoryPills from "../../../shared/components/CategoryPills/CategoryPills";
import Loading from "../../../shared/components/Loading/Loading";
import Modal from "../../../shared/components/Modal/Modal";
import { createPaymentLink } from "../../../features/paymentLinks/services/paymentLinkService";
import PaymentLinkResult from "../../../features/paymentLinks/components/PaymentLinkResult";
import "./VendaBalcaoPage.css";

// ─── constants ────────────────────────────────────────────────────────────────

const SEDE_ADDRESS = {
  street: "Rua Alexandre Siqueira, 175",
  complementary: "",
  street_number: "175",
  neighborhood: "Caiçaras",
  city: "Belo Horizonte",
  state: "MG",
  zipcode: "30775540",
  country: "br",
};

const PAYMENT_METHODS = [
  {
    value: "credit_card",
    label: "Cartão de crédito",
    shortcut: "F6",
    icon: "credit",
  },
  {
    value: "debit_card",
    label: "Cartão de débito",
    shortcut: "F7",
    icon: "debit",
  },
  { value: "pix", label: "PIX", shortcut: "F8", icon: "pix" },
  { value: "money", label: "Dinheiro", shortcut: "F9", icon: "money" },
  { value: "gift", label: "Vale presente", shortcut: "F10", icon: "gift" },
];

const QUICK_AMOUNTS = [50, 100, 200, 500];

function buildInitialOrder(mode) {
  // Venda por link é sempre remota (o cliente paga do outro lado do link,
  // não no balcão) — description "online" evita que essas vendas entrem
  // como "presencial" nas métricas do dashboard (ver
  // OrderController.getDashboardSummary, que classifica pelo texto de
  // description).
  const isLink = mode === "link";
  return {
    observations: "",
    userAdmin: null,
    type: "manually",
    description: isLink ? "online" : "presencial",
    products: [],
    backupProducts: [],
    changeCart: false,
    delivery: {
      name: "Svelta",
      fee: 0,
      delivery_date: "2020-12-09T12:48:53.017Z",
      expedited: false,
      type: isLink ? "correios" : "presencial",
      description: isLink ? "Entrega PAC à vista" : "Presencial",
      codeDelivery: isLink ? "1" : "P",
      address: { object: "address", ...SEDE_ADDRESS },
    },
    dataTransaction: moment(new Date()).format("YYYY-MM-DD"),
    amount: 0,
    paidAmount: 0,
    status: "paid",
    paymentMethod: "",
    installments: 1,
    customer: {
      object: "customer",
      type: "individual",
      country: "br",
      document_number: null,
      document_type: "cpf",
      name: "",
      email: "",
      phone_numbers: [""],
      born_at: null,
      birthday: null,
      gender: null,
      documents: [{ object: "document", type: "cpf", number: "" }],
    },
  };
}

// Adapta um pedido já existente (vindo de Invoices) para o formato de estado
// desta página, espelhando a conversão feita hoje em ModalEditOrderManually
// (tracking → delivery, centavos → reais, backup do carrinho original).
function mapExistingOrderToFormState(rawOrder) {
  const order = _.cloneDeep(rawOrder);

  order.delivery = order.delivery || {};
  order.delivery.type = rawOrder.tracking?.type ?? order.delivery.type;
  order.delivery.codeDelivery =
    rawOrder.tracking?.codeDelivery ?? order.delivery.codeDelivery;
  order.delivery.description =
    rawOrder.tracking?.description ?? order.delivery.description;
  order.delivery.fee = (order.delivery.fee || 0) / 100;

  order.backupProducts = _.cloneDeep(order.products || []);
  order.changeCart = false;
  // Pedidos antigos podem não ter esse campo salvo — sem o default, o
  // textarea nasce "não controlado" (value=undefined) e vira "controlado"
  // no meio do caminho assim que o usuário digita a 1ª letra, quebrando.
  order.observations = order.observations || "";

  order.customer = order.customer || {};
  order.customer.documents = order.customer.documents?.length
    ? order.customer.documents
    : [{ object: "document", type: "cpf", number: "" }];
  order.customer.documents[0].number = maskCPF(
    order.customer.documents[0].number || "",
  );

  const rawPhone = order.customer.phone_numbers?.[0] || "";
  const phoneDigits = rawPhone.replace(/\D/g, "");
  order.customer.phone_numbers = [
    phoneDigits.length > 11 && phoneDigits.startsWith("55")
      ? phoneDigits.slice(2)
      : phoneDigits,
  ];

  order.dataTransaction = moment(rawOrder.dataTransaction).format("YYYY-MM-DD");

  // order.amount/paidAmount vêm do backend em centavos (como delivery.fee);
  // em vez de converter, recalcula a partir dos produtos + frete já em
  // reais — mesma fonte da verdade usada por recalcTotals.
  const productsTotal = (order.products || []).reduce((acc, p) => {
    const price = p.ProductSale ? p.ProductSalePrice : p.ProductPrice;
    return acc + p.ProductQuantity * (price || 0);
  }, 0);
  order.amount = productsTotal + order.delivery.fee;
  order.paidAmount = order.amount;

  return order;
}

// ─── payment icon ─────────────────────────────────────────────────────────────

function PaymentIcon({ type }) {
  if (type === "credit" || type === "debit") return <LuCreditCard size={20} />;
  if (type === "gift") return <LuGift size={20} />;
  if (type === "more") return <LuEllipsis size={20} />;
  if (type === "pix")
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M12 3L3 12l9 9 9-9-9-9z" />
        <path d="M12 7.5L7.5 12 12 16.5 16.5 12 12 7.5z" />
      </svg>
    );
  if (type === "money")
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <circle cx="12" cy="12" r="2.5" />
        <path d="M6 12h.01M18 12h.01" />
      </svg>
    );
  return <LuEllipsis size={20} />;
}

// ─── main component ───────────────────────────────────────────────────────────

export default function VendaBalcaoPage({
  mode = "balcao",
  editOrderId = null,
  editOrderData = null,
}) {
  const history = useHistory();
  const isBalcao = mode === "balcao";
  const isEditMode = Boolean(editOrderId);
  // "link": gera um link de pagamento (Pagar.me) em vez de finalizar a
  // venda aqui — mesma seleção de produto/frete da tela normal, mas quem
  // escolhe forma de pagamento e (quando aplicável) preenche os dados é o
  // cliente do outro lado do link, não o admin.
  const isLinkMode = mode === "link";

  const [order, setOrder] = useState(() =>
    editOrderData
      ? mapExistingOrderToFormState(editOrderData)
      : buildInitialOrder(mode),
  );
  const [loading, setLoading] = useState(false);
  const [loadingVariant, setLoadingVariant] = useState(false);
  const [viewMode, setViewMode] = useState("grid");

  // product search & filter
  const [allProducts, setAllProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [productsFiltered, setProductsFiltered] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [productCosts, setProductCosts] = useState({});
  const debounceRef = useRef(null);

  // payment
  const [amountPaid, setAmountPaid] = useState(0);
  const [customAmount, setCustomAmount] = useState("");
  const [showCustomAmount, setShowCustomAmount] = useState(false);

  // venda por link (mode="link")
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  // modal open/close — internal state lives in each modal component
  const [discountOpen, setDiscountOpen] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerDataOpen, setCustomerDataOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [installmentOpen, setInstallmentOpen] = useState(false);

  const troco = Math.max(0, amountPaid - order.amount);

  const orderRef = useRef(order);
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  // ── init ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isEditMode && !editOrderData) {
      toast.error("Abra a edição a partir da lista de pedidos em Invoices.");
      history.replace("/admin-panel/Invoices");
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      try {
        const res = await requestApi(
          `/users/findById`,
          { idAuth: user.uid },
          "get",
          "user",
        );
        const docId =
          res && res.data && res.data.doc && res.data.doc._id
            ? res.data.doc._id
            : null;
        setOrder((prev) => ({ ...prev, userAdmin: docId }));
      } catch {
        /* usuário não encontrado no banco, continua sem userAdmin */
      }
    });
    loadAllProducts();
    loadProductCosts();
    window.scrollTo(0, 0);
  }, []); // eslint-disable-line

  // ── keyboard shortcuts ────────────────────────────────────────────────────

  // Always-current ref so the keydown closure (mounted once) calls the latest handleSave
  const handleSaveRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      const isFKey = /^F\d+$/.test(e.key);
      // Block regular keys when typing, but always allow F-keys (payment shortcuts etc.)
      if (
        !isFKey &&
        (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT")
      )
        return;
      const isReadOnlyCart = isEditMode && !orderRef.current.changeCart;
      switch (e.key) {
        case "F2":
          e.preventDefault();
          handleSaveRef.current();
          break;
        case "F3":
          e.preventDefault();
          if (isReadOnlyCart) break;
          if (orderRef.current.products.length > 0) setDiscountOpen(true);
          else toast.warning("Adicione produtos antes de aplicar desconto.");
          break;
        case "F4":
          e.preventDefault();
          if (isReadOnlyCart) break;
          setOrder((prev) => {
            if (!prev.products.length) return prev;
            const updated = [...prev.products];
            updated.splice(updated.length - 1, 1);
            return { ...prev, products: updated };
          });
          setTimeout(() => recalcTotals(), 80);
          break;
        case "F5":
          e.preventDefault();
          if (isReadOnlyCart) break;
          handleClearCart();
          break;
        case "F6":
          e.preventDefault();
          handleSelectPayment("credit_card");
          break;
        case "F7":
          e.preventDefault();
          handleSelectPayment("debit_card");
          break;
        case "F8":
          e.preventDefault();
          handleSelectPayment("pix");
          break;
        case "F9":
          e.preventDefault();
          handleSelectPayment("money");
          break;
        case "F10":
          e.preventDefault();
          handleSelectPayment("gift");
          break;
        case "F11":
          e.preventDefault();
          handleSelectPayment("not_applicable");
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  const loadAllProducts = async () => {
    try {
      const resp = await requestApi(`/products`, {}, "get");
      setAllProducts(resp.data.doc || []);
    } catch {
      /* silent */
    }
  };

  const loadProductCosts = async () => {
    try {
      const resp = await requestApi(`/totalCost`, {}, "get");
      const costs = {};
      (resp.data.doc || []).forEach((doc) => {
        if (doc.product && costs[doc.product] === undefined) {
          costs[doc.product] = doc.price;
        }
      });
      setProductCosts(costs);
    } catch {
      /* silent — painel de lucro só some se não achar custo */
    }
  };

  // ── totals ────────────────────────────────────────────────────────────────

  const recalcTotals = useCallback(() => {
    setOrder((prev) => {
      const productsTotal = prev.products.reduce((acc, p) => {
        const price = p.ProductSale ? p.ProductSalePrice : p.ProductPrice;
        return acc + p.ProductQuantity * (price || 0);
      }, 0);
      const total = productsTotal + (prev.delivery.fee || 0);
      return { ...prev, amount: total, paidAmount: total };
    });
  }, []);

  // ── displayed products (search + category filter) ─────────────────────────

  const getDisplayedProducts = () => {
    let base = allProducts;
    if (activeCategory !== "all") {
      base = base.filter((p) => p.category === activeCategory);
    }
    if (searchTerm.length >= 3) return productsFiltered;
    return base.slice(0, 12);
  };

  const displayedProducts = getDisplayedProducts();

  // ── product search ────────────────────────────────────────────────────────

  const findProductsByTerm = async (term) => {
    if (term.length < 3) {
      setProductsFiltered([]);
      return;
    }

    let list = allProducts;
    if (_.isEmpty(list)) {
      try {
        const resp = await requestApi(`/products`, {}, "get");
        list = resp.data.doc || [];
        setAllProducts(list);
      } catch {
        toast.error("Erro ao buscar produtos.");
        return;
      }
    }

    if (activeCategory !== "all") {
      list = list.filter((p) => p.category === activeCategory);
    }

    const validateZPL = routeZblInput(term);
    if (validateZPL.type === "ZBL_TAG") {
      try {
        const response = await get(`/products/findByBarcode`, {
          code128: validateZPL.raw,
        });
        const filtered = list.filter(
          (p) => p._id === response?.data?.product?._id,
        );
        if (response?.data?.product && filtered[0]) {
          addProductByBarcode(response.data, filtered[0]);
          setSearchTerm("");
          setProductsFiltered([]);
        }
      } catch {
        toast.error("Erro ao buscar produto por código de barras.");
      }
      return;
    }

    setProductsFiltered(
      list.filter(
        (p) =>
          p.code?.toUpperCase().includes(term.toUpperCase()) ||
          p.name?.toUpperCase().includes(term.toUpperCase()),
      ),
    );
  };

  const onSearchChange = (value) => {
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => findProductsByTerm(value), 300);
  };

  const onCategoryChange = (cat) => {
    setActiveCategory(cat);
    setSearchTerm("");
    setProductsFiltered([]);
  };

  // ── add / remove products ─────────────────────────────────────────────────

  const addProduct = (product) => {
    const colors = getColorsProduct(product);
    setOrder((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          ProductID: product._id,
          ProductCode: product.code || "",
          ProductName: product.name,
          ProductImage: product.variants?.[0]?.pictures?.[0]?.url || "",
          ProductQuantity: 1,
          ProductColor: {},
          stockID: "",
          variantID: "",
          ProductPrice: product.price,
          ProductSize: "",
          ProductNew: product.new,
          ProductSale: false,
          ProductSalePrice: product.salePrice,
          ProductOriginalPrice: product.price,
          colors,
          variants: product.variants,
          sizes: ["PP", "P", "M", "G", "GG", "XGG"],
        },
      ],
    }));
    setTimeout(recalcTotals, 80);
  };

  const addProductByBarcode = (barcodePayload, filteredProduct) => {
    setLoadingVariant(true);
    const base = buildBaseProductFromBarcode(
      barcodePayload,
      filteredProduct,
      getColorsProduct,
    );
    const normalized = normalizeOrderProduct(base);
    setOrder((prev) => ({
      ...prev,
      products: upsertOrderProduct(prev.products, normalized),
    }));
    setLoadingVariant(false);
    setTimeout(recalcTotals, 80);
  };

  const removeProduct = (index) => {
    setOrder((prev) => {
      const updated = [...prev.products];
      updated.splice(index, 1);
      return { ...prev, products: updated };
    });
    setTimeout(recalcTotals, 80);
  };

  const handleClearCart = () => {
    setOrder((prev) => ({ ...prev, products: [], amount: 0, paidAmount: 0 }));
    setAmountPaid(0);
  };

  // ── variant / stock ───────────────────────────────────────────────────────

  const changeVariant = async (variantItem, index) => {
    setLoadingVariant(true);
    const variantFound = order.products[index].variants?.find(
      (v) => v._id === variantItem.idVariant,
    );
    if (!variantFound) {
      setLoadingVariant(false);
      return;
    }

    setOrder((prev) => {
      const updated = [...prev.products];
      const p = { ...updated[index] };
      p.variantID = variantItem.idVariant;
      p.ProductColor = {
        color: variantFound.color?.hex,
        colorName: variantFound.colorName,
        idVariant: variantItem.idVariant,
      };
      p.ProductImage = variantFound.pictures?.[0]?.url || p.ProductImage;
      p.variant = variantFound;
      updated[index] = p;
      return { ...prev, products: updated };
    });

    try {
      const url = `/stocks?variantId=${variantFound._id}`;
      const resp = await requestApi(url, {}, "get");
      const { total, stock } = resp.data;
      setOrder((prev) => {
        const updated = [...prev.products];
        const p = { ...updated[index] };
        p.variant = { ...p.variant, stock };
        p.hasStock = total > 0;
        if (!p.hasStock) toast.error("Sem estoque para essa variante.");
        updated[index] = p;
        return { ...prev, products: updated };
      });
    } catch {
      toast.error("Erro ao buscar estoque.");
    }
    setLoadingVariant(false);
  };

  const changeSize = (size, index) => {
    setOrder((prev) => {
      const updated = [...prev.products];
      const p = { ...updated[index] };
      const stockData = p.variant?.stock?.find((s) => s.size === size);
      if (stockData) {
        p.stockID = stockData._id;
        p.ProductSize = size;
        p.stockQuantity = stockData.quantity;
      }
      updated[index] = p;
      return { ...prev, products: updated };
    });
    recalcTotals();
  };

  const updateQty = (value, index) => {
    const qty = Math.max(1, Number(value));
    setOrder((prev) => {
      const updated = [...prev.products];
      updated[index] = { ...updated[index], ProductQuantity: qty };
      return { ...prev, products: updated };
    });
    recalcTotals();
  };

  // ── discount ──────────────────────────────────────────────────────────────

  const applyDiscount = ({ discountType, discountValue, discountTarget }) => {
    const val = parseFloat(discountValue);
    if (!val || val <= 0) {
      toast.warning("Informe um valor válido.");
      return;
    }

    setOrder((prev) => {
      const updated = prev.products.map((p, i) => {
        if (discountTarget !== "all" && String(i) !== String(discountTarget))
          return p;
        const base = p.ProductOriginalPrice || p.ProductPrice;
        const salePrice =
          discountType === "percent"
            ? Math.max(0, base * (1 - val / 100))
            : Math.max(0, base - val);
        return {
          ...p,
          ProductSale: true,
          ProductSalePrice: parseFloat(salePrice.toFixed(2)),
        };
      });
      return { ...prev, products: updated };
    });

    setTimeout(recalcTotals, 80);
    setDiscountOpen(false);
    toast.success("Desconto aplicado.");
  };

  // ── customer ──────────────────────────────────────────────────────────────

  const openCustomerModal = () => setCustomerModalOpen(true);

  const selectCustomer = (record) => {
    const addr = record.delivery?.address || record.billing?.address;
    setOrder((prev) => ({
      ...prev,
      customer: {
        ...prev.customer,
        name: record.customer?.name || "",
        email: record.customer?.email || "",
        birthday: record.customer?.birthday || "",
        phone_numbers: record.customer?.phone_numbers?.length
          ? record.customer.phone_numbers
          : [""],
        documents: record.customer?.documents?.length
          ? record.customer.documents
          : [{ type: "cpf", number: "" }],
      },
      ...(addr ? { shippingAddress: addr } : {}),
    }));
    setCustomerModalOpen(false);
  };

  const clearCustomer = () => {
    setOrder((prev) => ({
      ...prev,
      customer: {
        ...prev.customer,
        name: "",
        email: "",
        birthday: "",
        phone_numbers: [""],
        documents: [{ object: "document", type: "cpf", number: "" }],
      },
    }));
  };

  const updateCustomer = (field, value) =>
    setOrder((prev) => ({
      ...prev,
      customer: { ...prev.customer, [field]: value },
    }));

  const updateCustomerPhone = (value) =>
    setOrder((prev) => {
      const phones = [...prev.customer.phone_numbers];
      phones[0] = value;
      return { ...prev, customer: { ...prev.customer, phone_numbers: phones } };
    });

  const updateCustomerCpf = (value) =>
    setOrder((prev) => ({
      ...prev,
      customer: {
        ...prev.customer,
        documents: [{ ...prev.customer.documents[0], number: maskCPF(value) }],
      },
    }));

  // ── address ───────────────────────────────────────────────────────────────

  const saveAddress = (address) => {
    setOrder((prev) => ({ ...prev, shippingAddress: { ...address } }));
    setAddressModalOpen(false);
  };

  // ── payment ───────────────────────────────────────────────────────────────

  const handleSelectPayment = (value) => {
    if (isLinkMode) return; // forma de pagamento é escolhida pelo cliente no link
    if (INSTALLMENT_METHODS.includes(value)) {
      // começa em 1× — se o modal for fechado sem escolher, fica à vista
      // (antes herdava as parcelas da forma anterior)
      setOrder((prev) => ({ ...prev, paymentMethod: value, installments: 1 }));
      setInstallmentOpen(true);
      return;
    }
    setOrder((prev) => ({ ...prev, paymentMethod: value, installments: 1 }));
    if (value !== "money") {
      setAmountPaid(0);
      setShowCustomAmount(false);
    }
  };

  // Mantém a forma escolhida (antes forçava "credit_card", e PIX parcelado
  // era salvo como cartão). Troco só faz sentido em dinheiro à vista.
  const handleConfirmInstallment = (n) => {
    setOrder((prev) => ({ ...prev, installments: n }));
    if (orderRef.current.paymentMethod !== "money" || n > 1) {
      setAmountPaid(0);
      setShowCustomAmount(false);
    }
  };

  const applyQuickAmount = (amount) => {
    setAmountPaid(amount);
    setShowCustomAmount(false);
  };

  // ── troca de carrinho (edição) ───────────────────────────────────────────

  const handleToggleChangeCart = (checked) => {
    setOrder((prev) => ({
      ...prev,
      changeCart: checked,
      // Ao ativar a edição, parte do carrinho original (marcado como
      // `_original`) em vez de zerar — assim dá pra remover itens antigos e
      // ver os novos separados, em vez de montar tudo do zero.
      products: checked
        ? _.cloneDeep(prev.backupProducts).map((p) => ({ ...p, _original: true }))
        : prev.backupProducts,
    }));
    setTimeout(recalcTotals, 80);
  };

  // ── detalhes da venda (tipo de venda / transporte / serviço / frete) ────

  const handleSaleType = (value) =>
    setOrder((prev) => ({ ...prev, description: value }));

  const handleTransportType = (value) =>
    setOrder((prev) => ({
      ...prev,
      delivery: { ...prev.delivery, type: value },
    }));

  const handleServiceType = (option) =>
    setOrder((prev) => ({
      ...prev,
      delivery: {
        ...prev.delivery,
        codeDelivery: option.value,
        description: option.label,
      },
    }));

  const handleDeliveryFee = (fee) => {
    setOrder((prev) => ({
      ...prev,
      delivery: { ...prev.delivery, fee },
    }));
    setTimeout(recalcTotals, 80);
  };

  // ── validate & save ───────────────────────────────────────────────────────

  const validate = () => {
    if (_.isEmpty(order.products)) {
      toast.warning("Adicione pelo menos um produto.");
      return false;
    }
    for (const p of order.products) {
      if (!p.variantID) {
        toast.warning(`Selecione uma cor para: ${p.ProductName}`);
        return false;
      }
      if (!p.ProductSize) {
        toast.warning(`Selecione um tamanho para: ${p.ProductName}`);
        return false;
      }
    }
    if (!isLinkMode && !order.paymentMethod) {
      toast.warning("Selecione o método de pagamento.");
      return false;
    }
    // se preencheu algum dado de cliente mas não o nome
    const hasPartialCustomer =
      order.customer.email ||
      (order.customer.phone_numbers[0] || "").replace(/\D/g, "") ||
      (order.customer.documents[0]?.number || "").replace(/\D/g, "");
    if (hasPartialCustomer && !order.customer.name.trim()) {
      toast.warning("Informe o nome do cliente.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = _.cloneDeep(order);
      delete payload.backupProducts;
      delete payload.changeCart;

      if (!payload.customer.name) payload.customer.name = "Não informado";

      if (payload.customer.phone_numbers[0]) {
        const digits = payload.customer.phone_numbers[0].replace(/\D/g, "");
        payload.customer.phone_numbers[0] = digits
          ? `+55${digits.replace(/^55/, "")}`
          : "";
        if (!payload.customer.phone_numbers[0])
          payload.customer.phone_numbers = [];
      } else {
        payload.customer.phone_numbers = [];
      }

      if (payload.customer.documents[0]) {
        const num = payload.customer.documents[0].number.replace(/\D/g, "");
        if (num) {
          payload.customer.documents[0].number = num;
        } else {
          payload.customer.documents = [];
        }
      }

      // PIX/dinheiro parcelado → Pendente; o resto → Pago (ver saleStatus.js)
      payload.status = resolveSaleStatus(payload, editOrderData);
      if (payload.status === "pendent") payload.paidAmount = 0;

      payload.dataTransaction = moment(payload.dataTransaction)
        .add(6, "hours")
        .format("YYYY-MM-DD HH:mm:ss");

      const endpoint = isEditMode
        ? `/services/pagarme/updateOrderByAdminManually`
        : `/services/pagarme/createOrderByAdminManuallyWithOrigin`;

      const resp = await requestApi(endpoint, { order: payload }, "post");

      const respError = resp?.data?.error;
      if (respError || resp?.data?.success === false) {
        const msg = respError || resp?.data?.message || "Erro ao salvar venda.";
        toast.error(`Erro: ${msg}`);
        console.error("[VendaBalcao] save error response:", resp?.data);
        setLoading(false);
        return;
      }

      toast.success(
        isEditMode
          ? "Venda atualizada com sucesso!"
          : "Venda registrada com sucesso!",
      );
      setTimeout(() => history.push("/admin-panel/Invoices"), 600);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro inesperado ao salvar a venda.";
      toast.error(`Erro: ${msg}`);
    }
    setLoading(false);
  };

  // Gera o link de pagamento (Pagar.me v5) a partir do mesmo carrinho de
  // produtos e do frete escolhido em SaleDetailsSection — não salva Order
  // nenhum aqui, quem cria o registro (paid) é o webhook quando o cliente
  // pagar do outro lado do link.
  const handleGenerateLink = async () => {
    if (!validate()) return;
    setGeneratingLink(true);
    try {
      const delivery =
        order.delivery.fee > 0
          ? { type: order.delivery.type, fee: order.delivery.fee }
          : undefined;
      const link = await createPaymentLink(order.products, order.observations, delivery);
      setLinkResult(link);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Erro inesperado ao gerar o link.";
      toast.error(`Erro: ${msg}`);
    }
    setGeneratingLink(false);
  };

  const handleCloseLinkResult = () => {
    setLinkResult(null);
    setOrder(buildInitialOrder(mode));
  };

  const fmt = (v) =>
    `R$ ${Number(v || 0)
      .toFixed(2)
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;

  const customerName = order.customer.name;
  handleSaveRef.current = isLinkMode ? handleGenerateLink : handleSave;

  const cartCount = order.products.length;
  const subtotal = order.products.reduce(
    (a, p) =>
      a + p.ProductQuantity * (p.ProductOriginalPrice || p.ProductPrice),
    0,
  );
  const totalDiscount = order.products.reduce((a, p) => {
    if (!p.ProductSale) return a;
    return (
      a +
      p.ProductQuantity *
        ((p.ProductOriginalPrice || p.ProductPrice) - p.ProductSalePrice)
    );
  }, 0);

  // ── lucro real / markup ao vivo (evita fechar venda no prejuízo sem perceber) ──
  const itemsRevenue = order.products.reduce((a, p) => {
    const price = p.ProductSale ? p.ProductSalePrice : p.ProductPrice;
    return a + p.ProductQuantity * (price || 0);
  }, 0);
  const knownCostItems = order.products.filter(
    (p) => productCosts[getProductId(p)] !== undefined,
  );
  const itemsCost = knownCostItems.reduce(
    (a, p) => a + p.ProductQuantity * productCosts[getProductId(p)],
    0,
  );
  const itemsMissingCost = order.products.length - knownCostItems.length;
  const realProfit = itemsRevenue - itemsCost;
  const markupUsed = itemsCost > 0 ? (itemsRevenue / itemsCost - 1) * 100 : null;
  const profitTone =
    Math.abs(realProfit) < 0.01
      ? "neutral"
      : realProfit > 0
      ? "positive"
      : "negative";

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="pos-page">
      <ToastContainer />

      {/* ── TOP BAR ─────────────────────────────────────────── */}
      <div className="pos-topbar">
        <div className="pos-topbar-left">
          <div>
            <h2 className="pos-title">
              {isEditMode
                ? "Editar Venda"
                : isLinkMode
                  ? "Venda por Link"
                  : "Venda Presencial"}
            </h2>
            <p className="pos-subtitle">
              {isEditMode
                ? "Altere os dados do pedido e salve para atualizar a venda"
                : isLinkMode
                  ? "Escolha os produtos e o frete para gerar o link de pagamento"
                  : "Bipe ou pesquise um produto para iniciar a venda"}
            </p>
          </div>
          <span className="pos-status-dot" />
          <span className="pos-status-label">Online</span>
        </div>
      </div>

      <div className="pos-body">
        {/* ══════════════ LEFT — Product search ══════════════ */}
        <div className="pos-left">
          {/* Search bar */}
          <div className="pos-search-wrap">
            <LuSearch size={17} className="pos-search-icon" />
            <input
              className="pos-search-input"
              placeholder="Pesquisar por produto, referência ou bipar código de barras"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
            />
            <button className="pos-scan-btn" title="Bipar código">
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path d="M4 7V5a1 1 0 0 1 1-1h2M4 17v2a1 1 0 0 0 1 1h2M17 4h2a1 1 0 0 1 1 1v2M17 20h2a1 1 0 0 0 1-1v-2" />
                <line x1="7" y1="8" x2="7" y2="16" />
                <line x1="10" y1="8" x2="10" y2="16" />
                <line x1="13" y1="8" x2="13" y2="16" />
                <line x1="16" y1="8" x2="16" y2="16" />
              </svg>
            </button>
          </div>

          {/* Category pills */}
          <CategoryPills
            categories={PRODUCT_CATEGORIES}
            activeCategory={activeCategory}
            onChange={onCategoryChange}
            className="pos-pills-padding"
          />

          {/* Products */}
          <div className="pos-products-section">
            <div className="pos-products-header">
              <h3 className="pos-products-title">
                {searchTerm.length >= 3
                  ? `Resultados (${productsFiltered.length})`
                  : activeCategory !== "all"
                    ? PRODUCT_CATEGORIES.find((c) => c.value === activeCategory)
                        ?.label
                    : "Produtos recentes"}
              </h3>
              <div className="pos-view-toggle">
                <button
                  className={`pos-view-btn${viewMode === "grid" ? " active" : ""}`}
                  onClick={() => setViewMode("grid")}
                >
                  <LuLayoutGrid size={14} />
                </button>
                <button
                  className={`pos-view-btn${viewMode === "list" ? " active" : ""}`}
                  onClick={() => setViewMode("list")}
                >
                  <LuList size={14} />
                </button>
              </div>
            </div>

            {searchTerm.length >= 3 && productsFiltered.length === 0 ? (
              <p className="pos-no-results">Nenhum produto encontrado.</p>
            ) : (
              <div
                className={
                  viewMode === "grid"
                    ? "pos-products-grid"
                    : "pos-products-list"
                }
              >
                {displayedProducts.map((p) => (
                  <div
                    key={p._id}
                    className={
                      viewMode === "grid"
                        ? "pos-product-card"
                        : "pos-product-row"
                    }
                    onClick={() => addProduct(p)}
                  >
                    <div className="pos-product-img-wrap">
                      <img
                        src={p.variants?.[0]?.pictures?.[0]?.url}
                        alt={p.name}
                        className="pos-product-img"
                      />
                      <div className="pos-product-add-overlay">+</div>
                    </div>
                    <div className="pos-product-info">
                      <span className="pos-product-name">{p.name}</span>
                      <span className="pos-product-ref">REF. {p.code}</span>
                      <span className="pos-product-price">{fmt(p.price)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ══════════════ RIGHT — scroll top + fixed payment footer ══════════════ */}
        <div className="pos-right">
          {/* scrollable area: cliente, carrinho, totais, finalizar */}
          <div className="pos-right-scroll">
            {/* Cliente */}
            <div className="pos-customer-section">
              <h4 className="pos-section-label">Cliente</h4>
              {isLinkMode ? (
                <div className="pos-customer-locked">
                  Carregado automaticamente após o pagamento do link
                </div>
              ) : customerName ? (
                <div
                  className="pos-customer-card"
                  onClick={() => setCustomerDataOpen(true)}
                >
                  <div className="pos-customer-avatar">
                    {customerName.charAt(0).toUpperCase()}
                  </div>
                  <span className="pos-customer-name">{customerName}</span>
                  <button
                    className="pos-customer-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearCustomer();
                    }}
                  >
                    <LuX size={13} />
                  </button>
                </div>
              ) : (
                <>
                  <div
                    className="pos-customer-search-wrap"
                    onClick={openCustomerModal}
                  >
                    <input
                      className="pos-customer-input"
                      placeholder="Digite o CPF ou nome do cliente"
                      readOnly
                    />
                    <LuSearch size={15} className="pos-customer-search-icon" />
                  </div>
                  <button
                    className="pos-add-customer-btn"
                    onClick={openCustomerModal}
                  >
                    + Adicionar cliente
                  </button>
                </>
              )}
              {!isLinkMode && (
                <button
                  className="pos-add-address-btn"
                  onClick={() => setAddressModalOpen(true)}
                >
                  {order.shippingAddress?.street
                    ? `Endereço: ${order.shippingAddress.street}, ${order.shippingAddress.street_number}`
                    : "+ Adicionar endereço"}
                </button>
              )}
            </div>

            {/* Observações */}
            <div className="pos-observations-wrap">
              <label className="pos-observations-label">Observações</label>
              <textarea
                className="pos-observations-input"
                placeholder="Adicione uma observação ao pedido..."
                value={order.observations}
                onChange={(e) =>
                  setOrder((prev) => ({
                    ...prev,
                    observations: e.target.value,
                  }))
                }
                rows={2}
              />
            </div>

            {/* Detalhes da venda — tipo de venda, transporte, serviço, frete */}
            {(isEditMode || isLinkMode) && (
              <SaleDetailsSection
                saleType={order.description}
                transportType={order.delivery.type}
                serviceType={order.delivery.codeDelivery}
                deliveryFee={order.delivery.fee}
                onSaleTypeChange={handleSaleType}
                onTransportTypeChange={handleTransportType}
                onServiceTypeChange={handleServiceType}
                onDeliveryFeeChange={handleDeliveryFee}
              />
            )}

            {/* Itens da venda */}
            <div className="pos-cart-section">
              <div className="pos-cart-header">
                <span className="pos-section-label">
                  Itens da venda
                  {cartCount > 0 && (
                    <span className="pos-cart-badge">{cartCount}</span>
                  )}
                </span>
                {!(isEditMode && !order.changeCart) && cartCount > 0 && (
                  <button
                    className="pos-clear-cart"
                    onClick={handleClearCart}
                    title="Limpar sacola"
                  >
                    <LuTrash2 size={15} />
                  </button>
                )}
              </div>

              {isEditMode && (
                <div className="pos-changecart-row">
                  <span className="pos-changecart-label">
                    Trocar carrinho de compras?
                  </span>
                  <Switch
                    checked={order.changeCart}
                    onChange={handleToggleChangeCart}
                    onColor="#111"
                    height={20}
                    width={40}
                    checkedIcon={false}
                    uncheckedIcon={false}
                  />
                </div>
              )}

              {isEditMode && !order.changeCart ? (
                order.backupProducts.length === 0 ? (
                  <p className="pos-cart-empty">Nenhum produto neste pedido.</p>
                ) : (
                  <div className="pos-cart-items">
                    {order.backupProducts.map((p, i) => (
                      <div
                        key={i}
                        className="pos-cart-item pos-cart-item--readonly"
                      >
                        <div className="pos-cart-item-img-wrap">
                          <img
                            src={p.ProductImage}
                            alt={p.ProductName}
                            className="pos-cart-item-img"
                          />
                          <span className="pos-qty-badge">
                            {p.ProductQuantity}
                          </span>
                        </div>
                        <div className="pos-cart-item-body">
                          <div className="pos-cart-item-row">
                            <span className="pos-cart-item-name">
                              {p.ProductName}
                            </span>
                          </div>
                          {p.ProductCode && (
                            <span className="pos-cart-item-ref">
                              REF. {p.ProductCode}
                            </span>
                          )}
                          <span className="pos-cart-item-variant">
                            {p.ProductColor?.colorName
                              ? `Cor: ${p.ProductColor.colorName}`
                              : "Cor: —"}
                            {p.ProductSize
                              ? ` | Tam: ${p.ProductSize}`
                              : " | Tam: —"}
                          </span>
                          <span className="pos-cart-item-price">
                            {fmt(
                              (p.ProductSale
                                ? p.ProductSalePrice
                                : p.ProductPrice) * p.ProductQuantity,
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : cartCount === 0 ? (
                <p className="pos-cart-empty">Nenhum produto adicionado.</p>
              ) : (
                <div className="pos-cart-items">
                  {order.products.map((p, i) => {
                    const showAddedDivider =
                      isEditMode &&
                      !p._original &&
                      (i === 0 || order.products[i - 1]._original);
                    return (
                    <React.Fragment key={i}>
                    {showAddedDivider && (
                      <div className="pos-cart-divider">
                        <span>Itens adicionados</span>
                      </div>
                    )}
                    <div className="pos-cart-item">
                      {/* Imagem com badge de quantidade */}
                      <div className="pos-cart-item-img-wrap">
                        <img
                          src={p.ProductImage}
                          alt={p.ProductName}
                          className="pos-cart-item-img"
                        />
                        <span className="pos-qty-badge">
                          {p.ProductQuantity}
                        </span>
                      </div>

                      <div className="pos-cart-item-body">
                        <div className="pos-cart-item-row">
                          <span className="pos-cart-item-name">
                            {p.ProductName}
                          </span>
                          <button
                            className="pos-cart-item-remove"
                            onClick={() => removeProduct(i)}
                          >
                            <LuX size={12} />
                          </button>
                        </div>
                        {p.ProductCode && (
                          <span className="pos-cart-item-ref">
                            REF. {p.ProductCode}
                          </span>
                        )}
                        <span className="pos-cart-item-variant">
                          {p.ProductColor?.colorName
                            ? `Cor: ${p.ProductColor.colorName}`
                            : "Cor: —"}
                          {p.ProductSize
                            ? ` | Tam: ${p.ProductSize}`
                            : " | Tam: —"}
                        </span>

                        {/* Cores */}
                        <div className="pos-cart-select-row">
                          <span className="pos-cart-select-label">
                            Selecione a cor:
                          </span>
                          <div className="pos-cart-colors">
                            {p.colors?.map((c) => (
                              <button
                                key={c.idVariant}
                                className={`pos-color-dot${p.variantID === c.idVariant ? " active" : ""}`}
                                style={{
                                  backgroundImage: `url(${c.colorImage})`,
                                  backgroundSize: "cover",
                                }}
                                onClick={() => changeVariant(c, i)}
                                title={c.colorName}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Tamanhos */}
                        {p.variant && (
                          <div className="pos-cart-select-row">
                            <span className="pos-cart-select-label">
                              Selecione o tamanho:
                            </span>
                            <div className="pos-cart-sizes">
                              {p.sizes.map((s) => (
                                <button
                                  key={s}
                                  className={`pos-size-chip${p.ProductSize === s ? " active" : ""}`}
                                  onClick={() => changeSize(s, i)}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {p.hasStock === false && (
                          <span className="pos-no-stock">Sem estoque</span>
                        )}

                        {/* Controles de quantidade */}
                        <div className="pos-cart-qty-row">
                          <div className="pos-cart-qty-wrap">
                            <button
                              className="pos-qty-btn"
                              onClick={() =>
                                updateQty(p.ProductQuantity - 1, i)
                              }
                            >
                              −
                            </button>
                            <span className="pos-qty-value">
                              {p.ProductQuantity}
                            </span>
                            <button
                              className="pos-qty-btn"
                              onClick={() =>
                                updateQty(p.ProductQuantity + 1, i)
                              }
                            >
                              +
                            </button>
                          </div>
                          <span className="pos-cart-item-price">
                            {fmt(
                              (p.ProductSale
                                ? p.ProductSalePrice
                                : p.ProductPrice) * p.ProductQuantity,
                            )}
                          </span>
                          {p.ProductSale && (
                            <span className="pos-cart-item-original">
                              {fmt(p.ProductOriginalPrice * p.ProductQuantity)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Totais */}
            <div className="pos-totals">
              <div className="pos-total-row">
                <span>Subtotal</span>
                <span>{fmt(subtotal)}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="pos-total-row pos-discount-row">
                  <span>Descontos</span>
                  <span>− {fmt(totalDiscount)}</span>
                </div>
              )}
              {order.delivery.fee > 0 && (
                <div className="pos-total-row">
                  <span>Frete</span>
                  <span>{fmt(order.delivery.fee)}</span>
                </div>
              )}
              <div className="pos-total-row pos-grand-total">
                <span>Total</span>
                <span>{fmt(order.amount)}</span>
              </div>
            </div>

            {/* Lucro real — visível na hora de decidir descontos */}
            {cartCount > 0 && (
              <div className="pos-profit-panel">
                <div className="pos-total-row">
                  <span>Preço de custo</span>
                  <span>{fmt(itemsCost)}</span>
                </div>
                <div
                  className={`pos-total-row pos-profit-row pos-profit-row--${profitTone}`}
                >
                  <span>Lucro real</span>
                  <span>{fmt(realProfit)}</span>
                </div>
                <div className="pos-total-row pos-markup-row">
                  <span>Markup usado</span>
                  <span>{markupUsed === null ? "—" : `${markupUsed.toFixed(0)}%`}</span>
                </div>
                {itemsMissingCost > 0 && (
                  <p className="pos-profit-warning">
                    Custo não cadastrado para {itemsMissingCost}{" "}
                    {itemsMissingCost === 1 ? "item" : "itens"} — valores acima
                    parciais.
                  </p>
                )}
              </div>
            )}

            {/* Finalizar + Ações — dentro do scroll, logo após os totais */}
            <div className="pos-finalize-wrap">
              <button
                className="pos-finalize-btn"
                onClick={isLinkMode ? handleGenerateLink : handleSave}
                disabled={isLinkMode ? generatingLink : loading}
              >
                {isLinkMode
                  ? generatingLink
                    ? "Gerando..."
                    : "Gerar link de pagamento"
                  : loading
                    ? "Salvando..."
                    : "Finalizar venda"}
                <kbd className="pos-kbd">F2</kbd>
              </button>

              {!(isEditMode && !order.changeCart) && (
                <div className="pos-quick-actions">
                  <button
                    className="pos-action-btn"
                    onClick={() => {
                      if (cartCount > 0) setDiscountOpen(true);
                      else toast.warning("Adicione produtos primeiro.");
                    }}
                  >
                    <LuPercent size={13} />
                    <span>Desconto</span>
                    <kbd className="pos-kbd-sm">F3</kbd>
                  </button>
                  <button
                    className="pos-action-btn"
                    onClick={() =>
                      cartCount > 0 && removeProduct(cartCount - 1)
                    }
                  >
                    <LuX size={13} />
                    <span>Cancelar item</span>
                    <kbd className="pos-kbd-sm">F4</kbd>
                  </button>
                  <button className="pos-action-btn" onClick={handleClearCart}>
                    <LuTrash2 size={13} />
                    <span>Cancelar venda</span>
                    <kbd className="pos-kbd-sm">F5</kbd>
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* end pos-right-scroll */}
        </div>
      </div>

      {/* ── FOOTER FIXO: formas de pagamento ── */}
      <div className={`pos-right-footer${isLinkMode ? " pos-right-footer--locked" : ""}`}>
        {isLinkMode && (
          <div className="pos-right-footer__lock-note">
            Bloqueado — a forma de pagamento é escolhida pelo cliente no link
          </div>
        )}
        <div className="pos-payment-section">
          <p className="pos-section-label">Finalizar pagamento</p>
          <div className="pos-payment-grid">
            {PAYMENT_METHODS.map((m) => (
              <button
                key={m.value}
                className={`pos-payment-card${order.paymentMethod === m.value ? " selected" : ""}`}
                onClick={() => handleSelectPayment(m.value)}
              >
                <PaymentIcon type={m.icon} />
                <span className="pos-payment-label">
                  {INSTALLMENT_METHODS.includes(m.value) &&
                  order.paymentMethod === m.value &&
                  order.installments > 1
                    ? `${m.label} ${order.installments}×`
                    : m.label}
                </span>
                <kbd className="pos-kbd-sm">{m.shortcut}</kbd>
              </button>
            ))}
          </div>
        </div>

        <div className="pos-quickpay-section">
          <div className="pos-quickpay-left">
            <p className="pos-section-label">Pagamento rápido</p>
            <div className="pos-quickpay-amounts">
              {QUICK_AMOUNTS.map((a) => (
                <button
                  key={a}
                  className={`pos-quickpay-btn${amountPaid === a ? " active" : ""}`}
                  onClick={() => applyQuickAmount(a)}
                >
                  R$ {a}
                </button>
              ))}
              <button
                className={`pos-quickpay-btn${showCustomAmount ? " active" : ""}`}
                onClick={() => {
                  setShowCustomAmount(true);
                  setAmountPaid(0);
                }}
              >
                Outro valor
              </button>
            </div>
            {showCustomAmount && (
              <input
                type="number"
                className="pos-custom-amount-input"
                placeholder="R$ 0,00"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setAmountPaid(parseFloat(e.target.value) || 0);
                }}
                autoFocus
              />
            )}
          </div>
          <div className="pos-troco">
            <p className="pos-section-label">Troco</p>
            <span className="pos-troco-value">{fmt(troco)}</span>
          </div>
        </div>
      </div>
      {/* end pos-right-footer */}

      <DiscountModal
        isOpen={discountOpen}
        onClose={() => setDiscountOpen(false)}
        products={order.products}
        onApply={applyDiscount}
      />

      <CustomerModal
        isOpen={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        customer={order.customer}
        onSelectCustomer={selectCustomer}
        onUpdateCustomer={updateCustomer}
        onUpdateCustomerPhone={updateCustomerPhone}
        onUpdateCustomerCpf={updateCustomerCpf}
      />

      <CustomerDataModal
        isOpen={customerDataOpen}
        onClose={() => setCustomerDataOpen(false)}
        customer={order.customer}
        onUpdateCustomer={updateCustomer}
        onUpdateCustomerPhone={updateCustomerPhone}
        onUpdateCustomerCpf={updateCustomerCpf}
      />

      <AddressModal
        isOpen={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        onSave={saveAddress}
        initialAddress={order.shippingAddress}
      />

      <InstallmentModal
        isOpen={installmentOpen}
        onClose={() => setInstallmentOpen(false)}
        total={order.amount}
        paymentMethod={order.paymentMethod}
        onConfirm={handleConfirmInstallment}
      />

      {isLinkMode && (
        <Modal isOpen={Boolean(linkResult)} onClose={handleCloseLinkResult} title="Link gerado">
          <PaymentLinkResult result={linkResult} />
          <button
            type="button"
            className="pos-finalize-btn"
            style={{ width: "100%", marginTop: 16 }}
            onClick={handleCloseLinkResult}
          >
            Gerar outro link
          </button>
        </Modal>
      )}

      {(loading || generatingLink) && <Loading overlay />}
    </div>
  );
}
